import type { ResourceSchemaColumn } from '@semanticedge/persistence-service-noheaders-client/src'

export type ResourceSchemaColumnEntry = [string, ResourceSchemaColumn]

export const SCHEMA_COLUMN_FLAG = {
	NULLABLE: 'nullable',
	PRIMARY_KEY: 'primaryKey'
} as const

export type SchemaColumnFlag = (typeof SCHEMA_COLUMN_FLAG)[keyof typeof SCHEMA_COLUMN_FLAG]

export const toOrderedColumnNames = (
	columns: Record<string, ResourceSchemaColumn> | undefined,
	columnOrder?: readonly string[]
): string[] => {
	const columnMap = columns ?? {}
	const orderedNames = columnOrder?.filter((name) => name in columnMap) ?? []
	const remainingNames = Object.keys(columnMap).filter((name) => !orderedNames.includes(name))

	return [...orderedNames, ...remainingNames]
}

export const toColumnEntries = (
	columns: Record<string, ResourceSchemaColumn> | undefined,
	columnOrder?: readonly string[]
): ResourceSchemaColumnEntry[] =>
	toOrderedColumnNames(columns, columnOrder).map((name) => [name, (columns ?? {})[name]])

export const toColumnRecord = (
	entries: readonly ResourceSchemaColumnEntry[]
): Record<string, ResourceSchemaColumn> => Object.fromEntries(entries)

export const toColumnOrder = (entries: readonly ResourceSchemaColumnEntry[]): string[] =>
	entries.map(([name]) => name)

export const getPrimaryKeyColumns = (
	columns: Record<string, ResourceSchemaColumn> | undefined
): string[] =>
	Object.entries(columns ?? {})
		.filter(([, column]) => column.primaryKey)
		.map(([name]) => name)

export const setColumnFlag = (
	entries: readonly ResourceSchemaColumnEntry[],
	columnName: string,
	flag: SchemaColumnFlag,
	value: boolean
): ResourceSchemaColumnEntry[] =>
	entries.map(
		([name, column]): ResourceSchemaColumnEntry =>
			name === columnName ? [name, { ...column, [flag]: value }] : [name, column]
	)
